import { api } from '../api.ts';

/**
 * Getting a browser to ring when the school has news.
 *
 * Three things have to line up and any of them can be missing: the browser must support push at
 * all, the person must allow it, and the server must have keys configured. Every function here
 * answers "not available" rather than throwing, because none of those is an error — a school
 * without push simply has the bell and the letter, as it did before.
 */

/** Where the service worker lives. Root scope, so it can be woken for any page of the school. */
const WORKER_URL = '/sw.js';

export type PushState = 'unsupported' | 'off' | 'on' | 'blocked';

/** Whether this browser can do any of it. Safari below 16.4 and most in-app browsers cannot. */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Registers the worker, quietly.
 *
 * Called on every start rather than only when subscribing: the worker is also what shows a
 * notification for a subscription made on a previous visit, so it has to exist before anybody
 * asks for anything.
 */
export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;

  try {
    return await navigator.serviceWorker.register(WORKER_URL, { scope: '/' });
  } catch {
    return null;
  }
}

/** What the browser thinks right now, without asking anybody anything. */
export async function currentState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const existing = await registration?.pushManager.getSubscription();
    return existing ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

/** The server's public key arrives as base64url; the browser wants raw bytes. */
function keyToBytes(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/gu, '+').replace(/_/gu, '/');
  const raw = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);

  return buffer;
}

/**
 * Asks for permission and tells the server where to send.
 *
 * Only ever called from a click. A permission prompt that appears on page load is the fastest way
 * to be refused for ever: browsers remember a refusal, and there is no second chance to explain
 * what the prompt was for.
 */
export async function turnOn(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const { key } = await api.pushKey();
  if (!key) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';

  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerWorker());
  if (!registration) return 'off';

  const subscription = await registration.pushManager.subscribe({
    // Required by every browser: a push nobody is shown is not allowed to be sent.
    userVisibleOnly: true,
    applicationServerKey: keyToBytes(key),
  });

  await api.pushSubscribe(subscription.toJSON() as Record<string, unknown>);

  return 'on';
}

/** Stops this browser ringing. The permission stays granted, so turning it back on is one click. */
export async function turnOff(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (subscription) {
    await api.pushUnsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe().catch(() => undefined);
  }

  return 'off';
}
